import { CheckCircle } from '@mui/icons-material';
import { Button, ButtonBase, styled, Typography } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Page from '../Page';

const TextButton = styled(ButtonBase)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.secondary.main,
  textDecoration: 'underline',
}));

const Content = styled(Column)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  textAlign: 'center',
});

type Props = {
  title: string;
  content: ReactNode;
  primaryButton: {
    label: string;
    onClick?: () => void;
    link?: string;
  };
  secondaryButton?: {
    label: string;
    onClick?: () => void;
    link?: string;
  };
  tertiaryButton?: {
    label: string;
    onClick?: () => void;
    link?: string;
  };
  buttonsLayout?: 'column' | 'row';
};

const SuccessPage: React.FC<Props> = ({
  title,
  content,
  primaryButton,
  secondaryButton,
  tertiaryButton,
  buttonsLayout = 'column',
}) => {
  const primaryButtonElement = primaryButton.link ? (
    <Link to={primaryButton.link}>
      <Button variant="contained" size="large" fullWidth>
        {primaryButton.label}
      </Button>
    </Link>
  ) : (
    <Button
      onClick={primaryButton.onClick}
      variant="contained"
      size="large"
      fullWidth
    >
      {primaryButton.label}
    </Button>
  );

  const secondaryButtonElement = secondaryButton?.link ? (
    <Link to={secondaryButton.link}>
      <Button variant="outlined" size="large" fullWidth>
        {secondaryButton.label}
      </Button>
    </Link>
  ) : secondaryButton ? (
    <Button
      onClick={secondaryButton.onClick}
      variant="outlined"
      size="large"
      fullWidth
    >
      {secondaryButton.label}
    </Button>
  ) : null;

  const tertiaryButtonElement = tertiaryButton?.link ? (
    <Link to={tertiaryButton.link}>
      <TextButton>{tertiaryButton.label}</TextButton>
    </Link>
  ) : tertiaryButton ? (
    <TextButton onClick={tertiaryButton.onClick}>
      {tertiaryButton.label}
    </TextButton>
  ) : null;

  return (
    <Page spacing="lg">
      <Content spacing="lg">
        <CheckCircle color="primary" fontSize="large" />
        <Column spacing="xs">
          <Typography variant="h3">{title}</Typography>
          <Typography variant="body2">{content}</Typography>
        </Column>
        <Column spacing="sm">
          {buttonsLayout === 'column' ? (
            <Column spacing="sm">
              {primaryButtonElement}
              {secondaryButtonElement}
            </Column>
          ) : (
            <Row spacing="sm">
              {primaryButtonElement}
              {secondaryButtonElement}
            </Row>
          )}
          {tertiaryButtonElement}
        </Column>
      </Content>
    </Page>
  );
};

export default SuccessPage;
